/**
 * Lane nudging pass.
 *
 * After orthogonal routing, multiple edges can produce segments
 * that share a channel — same y (horizontal) or same x (vertical)
 * with overlapping range on the other axis. Without nudging they
 * render on top of each other as a single thick line.
 *
 * This pass finds each channel cluster and nudges the segments into
 * evenly-spaced parallel lanes, centred around the original
 * coordinate. Only INTERIOR segments are nudged — segments touching
 * the polyline's first or last point stay pinned to the port point
 * so the edge still lands on the obstacle border.
 *
 * Obstacle-aware: the computed offset is CLAMPED to the safe range
 * bounded by any blockers whose other-axis range overlaps the
 * segment. A*'s original route was obstacle-free, so we need only
 * ensure the nudge doesn't push the segment into a strict blocker
 * interior. If the safe range is too narrow to accommodate the full
 * stack, members compress inward but never cross.
 *
 * Mutates the polylines in place.
 */

import type { Rect } from './types';

export interface LaneNudgeOptions {
  /** Spacing between lanes in the same cluster. Default 10. */
  spacing?: number;
  /** Segments shorter than this are skipped (too short to tell if
   *  they even visually collide). Default 16. */
  minSegmentLength?: number;
  /** Blocker rectangles — typically leaves (not frames). Used to
   *  clamp each lane offset so the nudge doesn't push a segment
   *  into a strict obstacle interior. Optional: if omitted, the
   *  nudger operates in pre-2026-04 geometry-only mode. */
  blockers?: ReadonlyArray<Rect>;
  /** Extra clearance to keep between a nudged segment and any
   *  blocker border, on top of the strict-interior check. Default 4. */
  obstacleClearance?: number;
}

interface Segment {
  edgeId: string;
  /** Index of the segment within its polyline. Segment j spans
   *  points j and j+1 of the flat `[x,y,x,y,...]` array. */
  segIdx: number;
  axis: 'h' | 'v';
  /** The constant coordinate (y for horizontal, x for vertical). */
  coord: number;
  /** Range on the other axis. */
  rangeMin: number;
  rangeMax: number;
}

export function nudgeLanes(
  routes: Map<string, number[]>,
  options: LaneNudgeOptions = {},
): void {
  const spacing = options.spacing ?? 10;
  const minLen = options.minSegmentLength ?? 16;
  const blockers = options.blockers ?? [];
  const obstacleClearance = options.obstacleClearance ?? 4;

  // ── Collect interior segments eligible for nudging ─────────
  const segments: Segment[] = [];
  for (const [edgeId, pts] of routes) {
    const pointCount = pts.length / 2;
    if (pointCount < 4) continue; // need at least one interior segment
    const lastPointIdx = pointCount - 1;
    const segCount = pointCount - 1;
    for (let j = 0; j < segCount; j++) {
      const startIdx = j;
      const endIdx = j + 1;
      // Skip segments touching either polyline endpoint — those are
      // pinned to the port points on source/target obstacles.
      if (startIdx === 0 || endIdx === lastPointIdx) continue;
      const x1 = pts[j * 2];
      const y1 = pts[j * 2 + 1];
      const x2 = pts[(j + 1) * 2];
      const y2 = pts[(j + 1) * 2 + 1];
      if (y1 === y2 && x1 !== x2) {
        if (Math.abs(x2 - x1) < minLen) continue;
        segments.push({
          edgeId, segIdx: j, axis: 'h',
          coord: y1,
          rangeMin: Math.min(x1, x2),
          rangeMax: Math.max(x1, x2),
        });
      } else if (x1 === x2 && y1 !== y2) {
        if (Math.abs(y2 - y1) < minLen) continue;
        segments.push({
          edgeId, segIdx: j, axis: 'v',
          coord: x1,
          rangeMin: Math.min(y1, y2),
          rangeMax: Math.max(y1, y2),
        });
      }
    }
  }

  // ── Group by (axis, coord) ─────────────────────────────────
  const byChannel = new Map<string, Segment[]>();
  for (const s of segments) {
    const key = `${s.axis}:${s.coord}`;
    let arr = byChannel.get(key);
    if (!arr) {
      arr = [];
      byChannel.set(key, arr);
    }
    arr.push(s);
  }

  // ── Per channel: cluster by overlap, assign lanes ──────────
  for (const channel of byChannel.values()) {
    if (channel.length < 2) continue;

    // Sort by rangeMin so overlaps are contiguous.
    channel.sort((a, b) => a.rangeMin - b.rangeMin);

    let cluster: Segment[] = [];
    let clusterMax = -Infinity;
    const flushCluster = (): void => {
      if (cluster.length >= 2) applyLanes(cluster, spacing, routes, blockers, obstacleClearance);
      cluster = [];
      clusterMax = -Infinity;
    };
    for (const s of channel) {
      if (s.rangeMin < clusterMax) {
        cluster.push(s);
        if (s.rangeMax > clusterMax) clusterMax = s.rangeMax;
      } else {
        flushCluster();
        cluster = [s];
        clusterMax = s.rangeMax;
      }
    }
    flushCluster();
  }
}

/**
 * Safe range for a nudge: `[lo, hi]` inclusive, giving the window
 * of coord values the segment can be moved to without crossing any
 * blocker whose OTHER-axis range overlaps this segment.
 *
 * "Overlaps" means strict overlap: a blocker that just touches the
 * segment's range endpoint doesn't force a detour (the segment
 * would pass along its border, not through its interior).
 */
function safeRange(
  segment: Segment,
  blockers: ReadonlyArray<Rect>,
  clearance: number,
): { lo: number; hi: number } {
  let lo = -Infinity;
  let hi = Infinity;
  for (const r of blockers) {
    if (segment.axis === 'h') {
      // Horizontal segment: check blockers whose x-range strictly
      // overlaps [rangeMin, rangeMax].
      if (segment.rangeMax <= r.x || segment.rangeMin >= r.x + r.width) continue;
      // Blocker spans y ∈ (r.y, r.y + r.height) in strict interior.
      // Our segment's coord must stay outside that.
      if (segment.coord <= r.y) {
        // Currently above the blocker → hi is capped by r.y - clearance.
        if (r.y - clearance < hi) hi = r.y - clearance;
      } else if (segment.coord >= r.y + r.height) {
        if (r.y + r.height + clearance > lo) lo = r.y + r.height + clearance;
      }
      // If segment.coord is inside the blocker's interior, the
      // original route was already broken — we leave lo/hi untouched
      // and the clamp below will preserve the current (broken) coord.
    } else {
      if (segment.rangeMax <= r.y || segment.rangeMin >= r.y + r.height) continue;
      if (segment.coord <= r.x) {
        if (r.x - clearance < hi) hi = r.x - clearance;
      } else if (segment.coord >= r.x + r.width) {
        if (r.x + r.width + clearance > lo) lo = r.x + r.width + clearance;
      }
    }
  }
  return { lo, hi };
}

function applyLanes(
  cluster: Segment[],
  spacing: number,
  routes: Map<string, number[]>,
  blockers: ReadonlyArray<Rect>,
  obstacleClearance: number,
): void {
  cluster.sort((a, b) => {
    const c = a.edgeId.localeCompare(b.edgeId);
    if (c !== 0) return c;
    return a.segIdx - b.segIdx;
  });

  const n = cluster.length;
  for (let i = 0; i < n; i++) {
    const offset = (i - (n - 1) / 2) * spacing;
    if (offset === 0) continue;
    const s = cluster[i];
    const pts = routes.get(s.edgeId);
    if (!pts) continue;

    // Compute the safe window. If the proposed coord falls inside
    // it, use it verbatim. Otherwise clamp — obstacle-avoidance
    // wins over perfect lane spacing.
    const proposed = s.coord + offset;
    const { lo, hi } = safeRange(s, blockers, obstacleClearance);
    const final = Math.max(lo, Math.min(hi, proposed));

    if (s.axis === 'h') {
      pts[s.segIdx * 2 + 1] = final;
      pts[(s.segIdx + 1) * 2 + 1] = final;
    } else {
      pts[s.segIdx * 2] = final;
      pts[(s.segIdx + 1) * 2] = final;
    }
  }
}
